/**
 * TradeStack - Auth Context
 *
 * Manages user authentication state
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('token');
      console.log('Auth: Validating token on mount, token exists:', !!token);

      if (!token) {
        console.log('Auth: No token found, setting loading false');
        setLoading(false);
        return;
      }

      try {
        console.log('Auth: Calling validate API...');
        const response = await authApi.validate();
        console.log('Auth: Validate response:', response.data);
        if (response.data.valid) {
          console.log('Auth: Token valid, setting user');
          setUser(response.data.user);
        } else {
          console.log('Auth: Token invalid, clearing storage');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } catch (err) {
        console.error('Auth: Validate error:', err.response?.status, err.message);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, []);

  // Register new user
  const register = async (userData) => {
    setError(null);
    try {
      const response = await authApi.register(userData);
      const { user: newUser, token } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Login
  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authApi.login({ email, password });
      const { user: loggedInUser, token } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(loggedInUser));
      setUser(loggedInUser);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Update profile
  const updateProfile = async (profileData) => {
    try {
      const response = await authApi.updateProfile(profileData);
      const updatedUser = response.data.user;

      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(prev => ({ ...prev, ...updatedUser }));

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Update failed';
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    updateProfile,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
